import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Music } from "lucide-react";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { FOOTER_DISCLAIMERS, BRAND_TAGLINES } from "@/data/brandUniverse";
import NewsletterSignup from "@/components/NewsletterSignup";

const LOGO_URL =
  "/brand/wet-cabbage-skegness.jpg";

export default function Footer() {
  const [tag, setTag] = useState(BRAND_TAGLINES[0]);
  const [disclaimer, setDisclaimer] = useState(FOOTER_DISCLAIMERS[0]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    setTag(BRAND_TAGLINES[Math.floor(Math.random() * BRAND_TAGLINES.length)]);
    setDisclaimer(
      FOOTER_DISCLAIMERS[Math.floor(Math.random() * FOOTER_DISCLAIMERS.length)]
    );
    const id = setInterval(() => {
      setTag(BRAND_TAGLINES[Math.floor(Math.random() * BRAND_TAGLINES.length)]);
    }, 4000);
    api
      .get("/collections")
      .then((r) => setCollections((r.data || []).filter((c) => c.product_count > 0)))
      .catch(() => setCollections([]));
    return () => clearInterval(id);
  }, []);

  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <footer
      className="border-t-2 border-[#1A3626] bg-[#0F0F0F] text-[#F9F9F4]"
      data-testid="site-footer"
    >
      <div className="border-b-2 border-[#1A3626] py-6">
        <Marquee gradient={false} speed={50} pauseOnHover>
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className="font-display mx-8 text-4xl font-black uppercase tracking-tight md:text-6xl wc-outline-text"
              style={{ WebkitTextStroke: "2px #93C065" }}
            >
              Wet Cabbage · {tag} ·
            </span>
          ))}
        </Marquee>
      </div>

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 py-16 md:grid-cols-2 md:px-10 lg:grid-cols-5">
        <div>
          <div className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Wet Cabbage — Skegness"
              className="h-auto w-44 object-contain mix-blend-screen md:w-52"
              data-testid="footer-logo"
            />
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#EBE7DD]">
            Premium personalised hoodies, crewnecks, caps and accessories.
            Inspired by travel, petrol stations, DIY chaos and not taking life
            too seriously.
          </p>
          <div
            className="mt-6 inline-block border-2 border-[#93C065] px-3 py-1 text-xs font-bold uppercase tracking-[0.25em] text-[#93C065]"
            data-testid="footer-rotating-tagline"
          >
            {tag}
          </div>
        </div>

        <FooterCol title="Shop">
          <FooterLink to="/shop">All Clothing</FooterLink>
          <FooterLink to="/shop">Shop</FooterLink>
          {collections.slice(0, 5).map((c) => (
            <FooterLink key={c.id} to={`/collections/${c.id}`}>
              {c.title}
            </FooterLink>
          ))}
        </FooterCol>

        <FooterCol title="Atelier">
          <FooterLink to="/about">About</FooterLink>
          <FooterLink to="/chronicle">The Cabbage Chronicle</FooterLink>
          <FooterLink to="/campaigns">Campaigns</FooterLink>
          <FooterLink to="/sponsorship">Underdog Sponsorship</FooterLink>
          <FooterLink to="/wholesale">Wholesale</FooterLink>
          <FooterLink to="/faq">FAQ</FooterLink>
        </FooterCol>

        <FooterCol title="Elsewhere">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noreferrer"
            className="footer-link flex items-center gap-2"
            data-testid="footer-instagram"
          >
            <Instagram className="h-4 w-4" strokeWidth={2.5} /> Instagram
          </a>
          <a
            href="https://tiktok.com"
            target="_blank"
            rel="noreferrer"
            className="footer-link flex items-center gap-2"
            data-testid="footer-tiktok"
          >
            <Music className="h-4 w-4" strokeWidth={2.5} /> TikTok
          </a>
          <a
            href="https://twitter.com"
            target="_blank"
            rel="noreferrer"
            className="footer-link flex items-center gap-2"
            data-testid="footer-twitter"
          >
            <Twitter className="h-4 w-4" strokeWidth={2.5} /> Twitter
          </a>
          <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#A2A98F]">
            @wetcabbage
          </div>
          <NewsletterSignup variant="inline" source="footer" className="mt-6" />
        </FooterCol>

        <FooterCol title="House">
          <p
            className="max-w-xs text-sm leading-relaxed text-[#EBE7DD]"
            data-testid="footer-company-blurb"
          >
            Wet Cabbage is an ambitious clothing project by{" "}
            <span className="text-[#F9F9F4]">Tipple Tours Ltd</span>, built
            somewhere between airports and pub gardens.
          </p>
          <address
            className="mt-5 not-italic text-xs leading-relaxed text-[#A2A98F]"
            data-testid="footer-company-address"
          >
            Tipple Tours Ltd
            <br />
            4th Floor, Silverstream House
            <br />
            Fitzroy Street
            <br />
            London, W1T 6EB
            <br />
            United Kingdom
          </address>
          <div className="mt-4 flex flex-col gap-1 text-xs text-[#EBE7DD]">
            <a
              href="tel:+442046345414"
              className="transition-colors hover:text-[#93C065]"
              data-testid="footer-company-phone"
            >
              +44 20 4634 5414
            </a>
            <a
              href="mailto:moist@wetcabbage.com"
              className="transition-colors hover:text-[#93C065]"
              data-testid="footer-company-email"
            >
              moist@wetcabbage.com
            </a>
          </div>
        </FooterCol>
      </div>

      <div className="border-t-2 border-[#1A3626] px-6 py-6 text-center md:px-10">
        <p
          className="font-display text-[11px] italic leading-snug tracking-wide text-[#EBE7DD]/70 md:text-xs"
          data-testid="footer-disclaimer"
        >
          {disclaimer}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.25em] text-[#A2A98F] md:text-xs">
          <span>© {year} Wet Cabbage</span>
          <span aria-hidden>·</span>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("wc:open-cookie-settings"))}
            className="transition-colors hover:text-[#EBE7DD]"
            data-testid="footer-cookie-settings"
          >
            Cookie preferences
          </button>
        </div>
      </div>

      <style>{`
        .footer-link{
          display:block;
          padding:4px 0;
          color:#EBE7DD;
          font-size:0.9rem;
          transition: color 150ms ease, transform 150ms ease;
        }
        .footer-link:hover{ color:#93C065; transform: translateX(3px); }
      `}</style>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="font-display mb-4 text-xs font-bold uppercase tracking-[0.3em] text-[#93C065]">
        {title}
      </div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link to={to} className="footer-link">
      {children}
    </Link>
  );
}
